import React, { useState, useEffect, useCallback } from 'react';
import { settingsApi } from '../../services/api';
import { getIngredientPrices, saveIngredientPrice, syncIngredientsFromProductionLog } from '../../services/productionLogService';
import AdminPageHeader from '../../components/AdminPageHeader';
import './Settings.css';

const DEFAULTS = {
  keg_billing_rate: '0.50',
  mileage_rate: '0.67',
};

function IngredientRow({ ingredient, onSave, saving }) {
  const [price, setPrice] = useState(ingredient.price_per_unit || '');
  const [unit, setUnit] = useState(ingredient.unit || 'lbs');

  return (
    <tr>
      <td>{ingredient.ingredient}</td>
      <td>
        <select value={unit} onChange={e => setUnit(e.target.value)} className="market-select" style={{width: 'auto'}}>
          <option value="lbs">lbs</option>
          <option value="oz">oz</option>
          <option value="quarts">quarts</option>
          <option value="gallons">gallons</option>
          <option value="cups">cups</option>
        </select>
      </td>
      <td>
        <div className="price-input-wrap" style={{width: 120}}>
          <span className="price-prefix">$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={e => setPrice(e.target.value)}
            className="price-input"
            placeholder="0.00"
          />
        </div>
      </td>
      <td>
        <button
          type="button"
          className="btn-primary"
          style={{padding: '4px 12px', fontSize: '0.82rem'}}
          onClick={() => onSave(ingredient.ingredient, unit, price)}
          disabled={saving}
        >
          Save
        </button>
      </td>
    </tr>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [ingredients, setIngredients] = useState([]);
  const [ingredientSaving, setIngredientSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsData, ingredientData] = await Promise.all([
        settingsApi.getAll(),
        getIngredientPrices(),
      ]);
      if (settingsData.settings) {
        setSettings(s => ({ ...s, ...settingsData.settings }));
      }
      if (ingredientData.success) {
        setIngredients(ingredientData.data || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleChange(e) {
    const { name, value } = e.target;
    setSettings(s => ({ ...s, [name]: value }));
  }

  async function handleSync() {
    setSyncing(true);
    const res = await syncIngredientsFromProductionLog();
    if (res.success) {
      const data = await getIngredientPrices();
      if (data.success) setIngredients(data.data || []);
    }
    setSyncing(false);
  }

  async function handleIngredientSave(ingredient, unit, price) {
    setIngredientSaving(true);
    await saveIngredientPrice(ingredient, unit, price);
    const data = await getIngredientPrices();
    if (data.success) setIngredients(data.data || []);
    setIngredientSaving(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await settingsApi.save(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <AdminPageHeader
        title="Settings"
        subtitle="Billing rates and system configuration"
      />
      <div className="page-body">
        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="loading-state">Loading settings...</div>
        ) : (
          <form onSubmit={handleSave}>
            <div className="settings-section">
              <h3 className="settings-section-title">Keg Billing</h3>
              <p className="settings-section-desc">Rate charged to Apple Ridge Farm per pound of keg product used at AR markets.</p>
              <div className="settings-field">
                <label>Billing Rate ($ per lb)</label>
                <div className="price-input-wrap" style={{width: 160}}>
                  <span className="price-prefix">$</span>
                  <input
                    name="keg_billing_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={settings.keg_billing_rate}
                    onChange={handleChange}
                    className="price-input"
                  />
                  <span className="price-suffix">/ lb</span>
                </div>
                <p className="settings-hint">
                  A full 5-gallon keg weighs ~55 lbs. At this rate a full keg would bill ${(parseFloat(settings.keg_billing_rate || 0) * 55).toFixed(2)}.
                </p>
              </div>
            </div>

            <div className="settings-section">
              <h3 className="settings-section-title">Mileage Reimbursement</h3>
              <p className="settings-section-desc">Rate CC pays AR per mile for markets where AR drives CC products.</p>
              <div className="settings-field">
                <label>Mileage Rate ($ per mile)</label>
                <div className="price-input-wrap" style={{width: 160}}>
                  <span className="price-prefix">$</span>
                  <input
                    name="mileage_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={settings.mileage_rate}
                    onChange={handleChange}
                    className="price-input"
                  />
                  <span className="price-suffix">/ mi</span>
                </div>
                <p className="settings-hint">Current IRS standard mileage rate is $0.67/mile.</p>
              </div>
            </div>

            <div className="settings-section">
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <h3 className="settings-section-title">AR Ingredient Prices</h3>
                <button type="button" className="btn-secondary" onClick={handleSync} disabled={syncing} style={{fontSize:'0.85rem'}}>
                  {syncing ? 'Syncing...' : '↻ Sync from Production Log'}
                </button>
              </div>
              <p className="settings-section-desc">Prices AR charges CC for ingredients used in production. These are used to auto-calculate intercompany billing.</p>
              {ingredients.length === 0 ? (
                <p className="settings-hint">No ingredients yet. Log batches with AR ingredients in the Production Log, then click Sync.</p>
              ) : (
                <table className="billing-table" style={{marginTop: 8}}>
                  <thead>
                    <tr><th>Ingredient</th><th>Unit</th><th>Price per Unit</th><th></th></tr>
                  </thead>
                  <tbody>
                    {ingredients.map((ing, i) => (
                      <IngredientRow key={i} ingredient={ing} onSave={handleIngredientSave} saving={ingredientSaving} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="settings-actions">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
              {saved && <span className="saved-msg">Settings saved!</span>}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
